import AccordionCustom from "@components/Accordion/AccordionCustom";
import FeatureCard, { FeatureCardItemProps } from "@components/FeatureCardV2";
import ImageGrid from "@components/ImageGrid";
import { CardDataDetailsProps } from "@components/LandingPageComponents/CardDataDetails";
import TestimonialSection from "@components/LandingPageComponents/TestimonialSection";
import ZohoHeader from "@components/LandingPageComponents/ZohoHeader";
import NewLayout from "@components/new-ui/Layout";
import YellowCTAhref from "@components/yellowCTAhref";
import { AccordionItemsProps } from "@pages/whatsapp-conversation-pricing-calculator";
import dynamic from "next/dynamic";

import schema from "@components/faqSchema/zoho-integration.json";
import ClientLogos from "@components/new-ui/ClientLogos";
import Head from "next/head";
const scriptText = JSON.stringify(schema, null, 2);

const cardDataCollection: CardDataDetailsProps[] = [
    {
        img: "https://assets.gallabox.com/gb-home/zoho-card-1.svg",
        title: "Sync contacts and deals",
        description: "Instantly sync your Zoho contacts and deals and engage them on WhatsApp by incorporating Gallabox into your Zoho workflows.",
    },
    {
        img: "https://assets.gallabox.com/gb-home/personalized-card.png",
        title: "Send personalized WhatsApp messages",
        description: "Send personalized messages enabling efficient sales and customer service. It also improves visibility and efficiency across the customer lifecycle.",
    },
    {
        img: "https://assets.gallabox.com/gb-home/zoho-card-3.svg",
        title: "Automate alerts and event-based triggers",
        description: "Automate WhatsApp notifications and trigger messages within your Zoho workflows based on specific customer actions and events.",
    },
    {
        img: "https://assets.gallabox.com/gb-home/zoho-card-4.svg",
        title: "Track customer engagement",
        description: "Track WhatsApp messages, sends, opens, and clicks in Gallabox for all your Zoho contacts and deals. Ensure no opportunity falls through the cracks.",
    },
];

// const FeatureVideoCardSection = dynamic(() => import("../../components/FeatureVideoCard"), {
//     ssr: false,
// });

const CardDataCarousal = dynamic(() => import("@components/LandingPageComponents/CardDataCarousal"), {
    ssr: false,
});

const featureCardInput: FeatureCardItemProps[] = [
    {
        chipTitle: "No code setup",
        title: "Integrate your WhatsApp to Zoho with just a few clicks",
        description:
            "Sync Zoho accounts and deals with Gallabox and streamline your WhatsApp customer engagement workflow. The predefined templates with module fields help you reach prospects and customers with the right message based on customer actions and events. Save time and improve your productivity.",

        mediaType: "video",
        mediaUrl: "https://files.gallabox.dev/64240fb9f07544be554d0ca0/60a6903a-56fc-45cb-8199-079e56f505fc-IntegrateyourWhatsApptoZoho.mp4",
        mediaAltText: "WhatsApp  Zoho integrations ",
        mediaPosition: "right",
    },
    {
        chipTitle: "Personalized WhatsApp messages",
        title: "Initiate WhatsApp conversations from within Zoho",
        description:
            "With Gallabox’s deep native integration with Zoho, you don’t need to leave your Zoho interface to send personalized WhatsApp messages to your Zoho contacts. Also, check the conversation history and get the context of WhatsApp engagements of each account within Zoho.",

        mediaType: "video",
        mediaUrl: "https://files.gallabox.dev/64240fb9f07544be554d0ca0/b3b0cc73-6d52-4fce-bc7e-48c232ead5af-InitiateWhatsAppconversationsfromwithinZoho.mp4",
        mediaAltText: "WhatsApp conversations ",
        mediaPosition: "left",
    },
    {
        chipTitle: "WhatsApp Broadcast Campaigns",
        title: "Broadcast WhatsApp campaigns at scale.",
        description:
            "Create segments of your Zoho contacts, and engage them on WhatsApp based on their deal stages. Run focused campaigns with open rates north of 90% with recommended products, new launches, and more.",

        mediaType: "video",
        mediaUrl: "https://files.gallabox.com/6449104a0a74778d292f255a/72b1e97d-cbc2-49b9-a843-0f786a0c8449-BroadcastWhatsAppcampaigns1.mp4",
        mediaAltText: "WhatsApp broadcast ",
        mediaPosition: "right",
    },
    {
        chipTitle: "WhatsApp Chatbots",
        title: "Collect data from conversations seamlessly and update it to Zoho",
        description:
            "Engage your prospects with our easy-to-use drag-and-drop chatbots. Ask targeted questions, run surveys, automate customer feedback, qualify potential leads without human intervention, and automatically update to Zoho.",

        mediaType: "image",
        mediaUrl: "https://assets.gallabox.com/gb-home/Collect-data.png",
        mediaAltText: "WhatsApp Chatbots ",
        mediaPosition: "left",
    },
    {
        chipTitle: "Shared Team Inbox",
        title: "Offer a seamless customer experience by democratizing customer context.",
        description:
            "With Gallabox’s team inbox, share the customer context of your Zoho contacts seamlessly across your team so that multiple agents can take over conversations of all your Zoho accounts without any context loss, offering a seamless customer experience.",

        mediaType: "image",
        mediaUrl: "https://assets.gallabox.com/gb-home/offer-seamless.png",
        mediaAltText: "Shared Team Inbox ",
        mediaPosition: "right",
    },
];

const accordionCustomInputItems: AccordionItemsProps[] = [
    {
        question: "Do I need coding knowledge for Zoho WhatsApp Integration?",
        answer: "No! You can integrate Zoho and WhatsApp in minutes with just a few clicks. In no time, you can send personalized WhatsApp messages and streamline communication for all your Zoho contacts.",
    },
    {
        question: "What type of campaigns can I run with WhatsApp marketing automation?",
        answer: "By integrating Gallabox and Zoho workflow, you can run various WhatsApp marketing automation campaigns, such as event-based triggers, personalized messages, follow-ups, payment notifications, and more, enhancing customer engagement and revenue.",
    },
    {
        question: "Can I customize WhatsApp messages for Zoho users?",
        answer: "Absolutely! Zoho workflows allow you to create custom workflows to configure triggers and actions for your Zoho contacts with tailored WhatsApp campaigns.",
    },
    {
        question: "Is WhatsApp for Zoho integration secure?",
        answer: "Yes! Integrating Zoho with Gallabox is a secure option to run WhatsApp campaigns for Zoho contacts. Gallabox and Zoho use encryption and follow security protocols and industry best practices to ensure your data is secured while being stored or transferred.",
    },
];

const zohoLanding: React.FC = () => {
    return (
        <NewLayout
            title="Zoho WhatsApp Integration to Automate CRM & Sales | Gallabox"
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/Zoho-og-image.jpg" }}
            canonical="https://gallabox.com/whatsapp-integrations/zoho"
            description="Seamlessly connect WhatsApp with Zoho CRM using Gallabox’s Zoho WhatsApp integration. Automate chats, boost sales, and manage leads in one place."
        >
            <Head>
                <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: scriptText }} />
            </Head>

            <div className="bg-white">
                <ZohoHeader />

                <div className="lg:max-w-7xl mx-auto">
                    <h3 className="text-base text-center font-normal py-2">Trusted by 10,000+ businesses</h3>
                    <ClientLogos />
                </div>

                <CardDataCarousal cardDataCollection={cardDataCollection} />

                <FeatureCard mobileView="content-first" cardData={featureCardInput} />

                <div className="lg:mt-20 mt-10">
                    <YellowCTAhref customText={<h3 className="text-2xl md:text-3xl font-bold text-yellow-800">Start engaging your Zoho contacts on WhatsApp today</h3>} />
                </div>
                <section className="max-w-6xl mx-auto mt-20  ">
                    <TestimonialSection
                        TestimonialSectionPropsData={[
                            {
                                avatarImg: "https://assets.gallabox.com/gb-home/medical-comp-owner.png",
                                companyImg: "https://assets.gallabox.com/gb-home/medical-comp-logo.png",
                                headerTitle:
                                    "The integration of WhatsApp API into Zoho CRM is truly impressive, providing businesses with the capability to send personalized messages to leads and customers directly from their CRM system.",
                                nameText: "Anubhav ",
                                professionText: "Medical Certificate, VP-Growth & Strategy",
                            },
                            {
                                avatarImg: "https://assets.gallabox.com/gb-home/antrik-comp-owner.png",
                                companyImg: "https://assets.gallabox.com/gb-home/antrik-comp-logo.png",

                                headerTitle:
                                    "Gallabox has a simple and friendly interface, and the Zoho integration is rather intuitive. I’ve had no problems using it, and the customer service is great.",
                                nameText: "Aman Thakur",
                                professionText: "Co-Founder, Aantrik",
                            },
                        ]}
                    />
                </section>
                <section className="w-full h-full pt-20" style={{ backgroundImage: "url(/Ellipse.svg)" }}>
                    <ImageGrid />
                </section>
                <section className="max-w-6xl py-4 px-4 lg:px-0 mx-auto pb-10">
                    <div style={{ borderRadius: "6px" }} className="w-full h-40  bg-yellow-200">
                        <div className="flex flex-col lg:gap-4 gap-2 justify-center items-center px-4 lg:px-0 lg:py-8 py-4">
                            <h3 className="text-yellow-800 font-bold text-center lg:text-3xl text-lg">Engage your Zoho contacts on WhatsApp today</h3>
                            <a
                                className="main-button outline-none focus:outline-none start-trial-btn hover:no-underline hover:shadow-lg transition duration-300 ease-in-out inline-flex items-center justify-center md:justify-start gap-1"
                                target="_blank"
                                href="https://app.gallabox.com/signup"
                                rel="noreferrer"
                            >
                                Get started Today
                            </a>
                        </div>
                    </div>
                </section>
                <div className="lg:mt-20 mt-14 mb-10">
                    <section className="max-w-6xl p-4 mx-auto">
                        <div className="flex flex-col justify-center lg:gap-4 gap-2 items-center pb-6">
                            <p className="lg:text-4xl text-3xl text-center font-bold text-gray-700">Frequently asked questions</p>
                            <h3 className="text-gray-500 text-center lg:text-2xl text-lg font-normal ">Everything you need to know about Zoho-Gallabox integration</h3>
                        </div>
                        <AccordionCustom title="Zoho-Gallabox integration" items={accordionCustomInputItems} />
                    </section>
                </div>
            </div>
        </NewLayout>
    );
};

export default zohoLanding;
